import axios from "axios";
import { API } from "../AuthContext";

export const O = `${API}/outreach`;

const TRANSIENT = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527]);
export const isTransient = (e) => !e?.response || TRANSIENT.has(e?.response?.status);
export const errText = (e) => {
  if (isTransient(e)) return "Server is restarting (usually a deployment) — this clears within a minute and the page reconnects on its own.";
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Request failed.";
};

export const fmtDateTime = (ts) => (ts
  ? new Date(ts).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
  : "—");

export const api = {
  overview: () => axios.get(`${O}/overview`).then((r) => r.data),
  contacts: (q = "", campaignId, page = 1) => axios.get(`${O}/contacts`, { params: { q, page, campaign_id: campaignId || undefined } }).then((r) => r.data),
  importFile: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${O}/contacts/import`, fd, { onUploadProgress: onProgress, timeout: 180000 }).then((r) => r.data);
  },
  researchStart: (body) => axios.post(`${O}/research/start`, body || {}).then((r) => r.data),
  researchStop: () => axios.post(`${O}/research/stop`).then((r) => r.data),
  redraft: (id) => axios.post(`${O}/contacts/${id}/redraft`).then((r) => r.data),
  researchCompany: (id) => axios.post(`${O}/companies/${id}/research`, {}, { timeout: 240000 }).then((r) => r.data),
  setHook: (id, asset_hook) => axios.put(`${O}/companies/${id}/hook`, { asset_hook }).then((r) => r.data),
  patchContact: (id, body) => axios.patch(`${O}/contacts/${id}`, body).then((r) => r.data),
  deleteContact: (id) => axios.delete(`${O}/contacts/${id}`).then((r) => r.data),
  bulk: (ids, action) => axios.post(`${O}/contacts/bulk`, { ids, action }).then((r) => r.data),
  bulkByQuery: (q, action) => axios.post(`${O}/contacts/bulk`, { q, action }).then((r) => r.data),
  deleteCompany: (companyKey) => axios.delete(`${O}/companies/${encodeURIComponent(companyKey)}`).then((r) => r.data),
  pause: (until, label) => axios.post(`${O}/pause`, { until, label }).then((r) => r.data),
  resume: () => axios.delete(`${O}/pause`).then((r) => r.data),
  blocklist: () => axios.get(`${O}/blocklist`).then((r) => r.data),
  unblockEmail: (email) => axios.delete(`${O}/blocklist/email/${encodeURIComponent(email)}`).then((r) => r.data),
  unblockDomain: (domain) => axios.delete(`${O}/blocklist/domain/${encodeURIComponent(domain)}`).then((r) => r.data),
  approveAll: () => axios.post(`${O}/approve-all`).then((r) => r.data),
  unapproveAll: () => axios.post(`${O}/unapprove-all`).then((r) => r.data),
  sendTests: () => axios.post(`${O}/send-tests`).then((r) => r.data),
  testSetStart: (n = 5, send = true) => axios.post(`${O}/test-set/start`, { n, send }).then((r) => r.data),
  roles: () => axios.get(`${O}/roles`).then((r) => r.data),
  roleAction: (role, action) => axios.post(`${O}/roles/action`, { role, action }).then((r) => r.data),
  batchStart: () => axios.post(`${O}/batch/start`).then((r) => r.data),
  batchStop: () => axios.post(`${O}/batch/stop`).then((r) => r.data),
  batchStatus: () => axios.get(`${O}/batch/status`).then((r) => r.data),
  syncReplies: () => axios.post(`${O}/replies/sync`).then((r) => r.data),
  sweepBounces: () => axios.post(`${O}/bounces/sweep`).then((r) => r.data),
  syncStatus: () => axios.get(`${O}/sync/status`).then((r) => r.data),
  deleteBounced: () => axios.delete(`${O}/bounced`).then((r) => r.data),
  purgeContacts: () => axios.delete(`${O}/campaign/contacts`).then((r) => r.data),
  setContext: (context) => axios.put(`${O}/campaign/context`, { context }).then((r) => r.data),
  setName: (name) => axios.put(`${O}/campaign/name`, { name }).then((r) => r.data),
  archive: (name) => axios.post(`${O}/campaign/archive`, { name }).then((r) => r.data),
  campaigns: () => axios.get(`${O}/campaigns`).then((r) => r.data),
  campaign: (id, page = 1) => axios.get(`${O}/campaigns/${id}`, { params: { page } }).then((r) => r.data),
  settings: () => axios.get(`${O}/settings`).then((r) => r.data),
  saveSettings: (body) => axios.put(`${O}/settings`, body).then((r) => r.data),
  suppression: () => axios.get(`${O}/suppression`).then((r) => r.data),
  removeSuppression: (email) => axios.delete(`${O}/suppression/${encodeURIComponent(email)}`).then((r) => r.data),
  addSuppression: (email, reason = "manual") => axios.post(`${O}/suppression`, { email, reason }).then((r) => r.data),
  audit: (limit = 100) => axios.get(`${O}/audit`, { params: { limit } }).then((r) => r.data),
  preview: (id) => axios.get(`${O}/preview/${id}`).then((r) => r.data),
  gmailConnect: () => axios.post(`${O}/gmail/connect`, { redirect_base: window.location.origin }).then((r) => r.data),
  gmailDisconnect: () => axios.post(`${O}/gmail/disconnect`).then((r) => r.data),
};

// "HH:MM" UTC ⇄ local wall-clock (today's offset) for the daily start-time control.
export const utcHHMMToLocal = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(); d.setUTCHours(h, m, 0, 0);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
export const localHHMMToUtc = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
};
export const fmtTime = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

export const STATUS_META = {
  new:         { label: "No draft",      cls: "border-[#E8E1D2] bg-white text-[#A09680]" },
  drafted:     { label: "Draft ready",   cls: "border-[#C84C21]/40 bg-[#FCE9E1] text-[#C84C21]" },
  approved:    { label: "Approved",      cls: "border-[#3F6D55]/40 bg-[#EAF3EC] text-[#2E4A38]" },
  sent:        { label: "Sent",          cls: "border-[#0F1825]/30 bg-[#F0EBDC] text-[#0F1825]" },
  replied:     { label: "Replied",       cls: "border-[#4E378A]/40 bg-[#EDE9F4] text-[#4E378A]" },
  bounced:     { label: "Bounced",       cls: "border-[#C84C21] bg-[#FCE9E1] text-[#C84C21]" },
  suppressed:  { label: "Do not contact", cls: "border-[#5C6470]/40 bg-[#EEF0F2] text-[#5C6470]" },
  unconfirmed: { label: "Unconfirmed",   cls: "border-[#B7791F] bg-[#FFF6E5] text-[#B7791F]" },
  error:       { label: "Send error",    cls: "border-[#C84C21] bg-white text-[#C84C21]" },
};

export const chipCls = "inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] border px-2 py-0.5";
export const btnDark = "inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-[10.5px] uppercase tracking-[0.18em] px-3.5 py-2.5 transition-colors";
export const btnLight = "inline-flex items-center gap-2 bg-white border border-[#E8E1D2] hover:border-[#0F1825] disabled:opacity-40 disabled:cursor-not-allowed text-[#0F1825] font-mono text-[10.5px] uppercase tracking-[0.18em] px-3.5 py-2.5 transition-colors";
export const btnAccent = "inline-flex items-center gap-2 bg-[#C84C21] hover:bg-[#0F1825] disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-[10.5px] uppercase tracking-[0.18em] px-3.5 py-2.5 transition-colors";
export const inputCls = "w-full bg-white border border-[#E8E1D2] px-3 py-2.5 text-[13px] text-[#0F1825] focus:outline-none focus:border-[#0F1825]";
export const labelCls = "block font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680] mb-1.5";
